import { Helmet } from 'react-helmet-async';

export function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Leka",
    "url": "https://lekalearning.com",
    "logo": "https://lekalearning.com/og-image.png",
    "description": "Language learning platform with built-in translation and FSRS spaced repetition — the same algorithm powering Anki 24+"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export function SoftwareApplicationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Leka",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web, iOS, Android",
    "offers": [
      {
        "@type": "Offer",
        "name": "Free",
        "price": "0",
        "priceCurrency": "USD"
      },
      {
        "@type": "Offer",
        "name": "Premium Monthly",
        "price": "5.99",
        "priceCurrency": "USD",
        "priceValidUntil": "2026-12-31"
      },
      {
        "@type": "Offer",
        "name": "Premium Yearly",
        "price": "60",
        "priceCurrency": "USD",
        "priceValidUntil": "2026-12-31"
      }
    ],
    "downloadUrl": "https://apps.apple.com/app/leka-learning/id6758303395",
    "description": "Master languages with FSRS-powered flashcards — the same algorithm used by Anki 24+ for optimal vocabulary retention",
    "featureList": [
      "6 Study Modes: Normal, Reversed, Image Association, Sentence Builder, Dictation, Read Aloud",
      "FSRS Spaced Repetition Algorithm (Same as Anki 24+)",
      "Built-in Translation for 8 Languages",
      "Listening & Speaking Practice",
      "Offline Vocabulary Review",
      "Verb Conjugation Practice",
      "Progress Tracking",
      "Example Sentences"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface FAQPageSchemaProps {
  faqs: { question: string; answer: string }[];
}

export function FAQPageSchema({ faqs }: FAQPageSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

// RFC-149: Breadcrumb structured data for inner marketing pages.
interface BreadcrumbItem {
  name: string;
  /** Absolute URL — pass `https://lekalearning.com${path}`. */
  url: string;
}

interface BreadcrumbListSchemaProps {
  items: BreadcrumbItem[];
}

export function BreadcrumbListSchema({ items }: BreadcrumbListSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// RFC-149: EducationalOrganization structured data for /schools.
export function EducationalOrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'Leka',
    url: 'https://lekalearning.com',
    logo: 'https://lekalearning.com/og-image.png',
    description:
      'Leka helps language schools and teachers bring spaced-repetition vocabulary review into the classroom. Pilot program for early-access teachers.',
    sameAs: [
      'https://apps.apple.com/app/leka-learning/id6758303395',
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
